/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Grid from "@mui/material/Grid";
import {
  Title,
  Datagrid,
  List,
  TextField as RATextField,
  FunctionField,
  useRedirect,
  usePermissions,
  Button as RAButton,
  type Exporter,
  type RaRecord,
} from "react-admin";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";

import { ExportButton } from "@/components/ExportButton";
import { formatListDate } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";

import { callApi } from "@/dataprovider/misc-apis";
import { exportYearlyLedgerToExcel } from "./yearly-ledger-export";

type LedgerType = {
  id: string;
  name: string;
  code: string;
  isActive?: boolean;
  bankDetails?: {
    bankName?: string | null;
    accountName?: string | null;
    accountNumber?: string | null;
    ifscCode?: string | null;
    branchName?: string | null;
    upiId?: string | null;
    notes?: string | null;
  } | null;
};

type Summary = {
  creditTotalCash?: number;
  creditTotalOnline?: number;
  debitTotalCash?: number;
  debitTotalOnline?: number;
  openingBalanceCash?: number;
  openingBalanceOnline?: number;
  closingBalanceCash?: number;
  closingBalanceOnline?: number;
  entryCount?: number;
};

export default function YearlyLedgerDashboard() {
  const redirect = useRedirect();
  const { permissions } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [ledgerTypes, setLedgerTypes] = useState<LedgerType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const initialTypeId = searchParams.get("yearlyLedgerTypeId");
  const [yearlyLedgerTypeId, setYearlyLedgerTypeId] = useState<string>(initialTypeId ?? "");
  const [paymentModeTab, setPaymentModeTab] = useState<"CASH" | "ONLINE">("CASH");
  const [financialYear, setFinancialYear] = useState<string>(() => {
    const fromUrl = searchParams.get("financialYear");
    if (fromUrl) return fromUrl;
    const now = dayjs();
    const month = now.month() + 1;
    const year = now.year();
    return month >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
  });
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<Summary | null>(null);

  const canView = hasPermission(permissions, "yearlyLedger.view");
  const canCreate = hasPermission(permissions, "yearlyLedger.create");

  const selectedType = useMemo(
    () => ledgerTypes.find((t) => t.id === yearlyLedgerTypeId) ?? null,
    [ledgerTypes, yearlyLedgerTypeId]
  );

  const exporter = (records: RaRecord[]) =>
    exportYearlyLedgerToExcel(records, {
      ledgerTypeName: selectedType?.name,
      ledgerTypeCode: selectedType?.code,
      financialYear,
      paymentMode: paymentModeTab,
    });

  useEffect(() => {
    setTypesLoading(true);
    void callApi({
      location: "yearly-ledger",
      method: "GET",
      id: "types",
    })
      .then((res) => {
        const rows = (res.data as { rows?: LedgerType[] })?.rows ?? [];
        setLedgerTypes(rows);
        if (rows.length === 0) return;
        const fromUrl =
          initialTypeId && rows.some((r) => r.id === initialTypeId) ? initialTypeId : null;
        setYearlyLedgerTypeId(fromUrl ?? rows[0].id);
      })
      .finally(() => setTypesLoading(false));
  }, []);

  const fetchLedgerReport = async () => {
    if (!financialYear || !yearlyLedgerTypeId) return;
    setLoading(true);
    const res = await callApi({
      location: "yearly-ledger",
      method: "GET",
      id: `ledger/report?financialYear=${encodeURIComponent(financialYear)}&yearlyLedgerTypeId=${encodeURIComponent(yearlyLedgerTypeId)}&paymentMode=${paymentModeTab}`,
    });
    setSummary(res.data as Summary);
    setLoading(false);
  };

  useEffect(() => {
    if (!yearlyLedgerTypeId) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("yearlyLedgerTypeId", yearlyLedgerTypeId);
      return next;
    });
  }, [yearlyLedgerTypeId, setSearchParams]);

  useEffect(() => {
    const typeFromUrl = searchParams.get("yearlyLedgerTypeId");
    if (typeFromUrl && ledgerTypes.some((r) => r.id === typeFromUrl)) {
      setYearlyLedgerTypeId(typeFromUrl);
    }
    const fyFromUrl = searchParams.get("financialYear");
    if (fyFromUrl) {
      setFinancialYear(fyFromUrl);
    }
  }, [searchParams, ledgerTypes]);

  useEffect(() => {
    if (!typesLoading && yearlyLedgerTypeId) {
      void fetchLedgerReport();
    }
  }, [financialYear, yearlyLedgerTypeId, paymentModeTab, typesLoading]);

  const generateFinancialYearOptions = () => {
    const options = [];
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month() + 1;
    for (let i = -5; i <= 2; i += 1) {
      const year = currentYear + i;
      if (i === 0 && currentMonth < 4) {
        options.push({ value: `${year - 1}-${year}`, label: `${year - 1}-${year}` });
      } else if (i === 0 && currentMonth >= 4) {
        options.push({ value: `${year}-${year + 1}`, label: `${year}-${year + 1}` });
      } else {
        const startYear = year;
        const endYear = year + 1;
        options.push({ value: `${startYear}-${endYear}`, label: `${startYear}-${endYear}` });
      }
    }
    return options.reverse();
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);

  const getFinancialYearDateRange = (fy: string) => {
    const [startYear, endYear] = fy.split("-").map(Number);
    return {
      startDate: `${startYear}-04-01`,
      endDate: `${endYear}-03-31`,
    };
  };

  const fyDateRange = getFinancialYearDateRange(financialYear);

  const createUrl = selectedType
    ? `/yearly-ledger/create?yearlyLedgerTypeId=${encodeURIComponent(selectedType.id)}&ledgerTypeName=${encodeURIComponent(selectedType.name)}&financialYear=${encodeURIComponent(financialYear)}`
    : "/yearly-ledger/create";

  const printEntry = (entryId: string | number) => {
    window.open(`#/yl-rcpt/${entryId}`, "_blank");
  };

  const printEntryField = (
    <FunctionField
      label="Print"
      sortable={false}
      render={(record) => (
        <RAButton onClick={() => printEntry(record.id)}>
          <DownloadIcon />
        </RAButton>
      )}
    />
  );

  const summaryCardsConfig = [
    {
      key: "creditTotal",
      label: "TOTAL CREDIT",
      getValue: (data: Summary, mode: string) =>
        mode === "CASH" ? data.creditTotalCash : data.creditTotalOnline,
      colorPositive: "success.main",
      colorNegative: "success.main",
    },
    {
      key: "debitTotal",
      label: "TOTAL DEBIT",
      getValue: (data: Summary, mode: string) =>
        mode === "CASH" ? data.debitTotalCash : data.debitTotalOnline,
      colorPositive: "error.main",
      colorNegative: "error.main",
    },
    {
      key: "openingBalance",
      label: "OPENING BALANCE",
      getValue: (data: Summary, mode: string) =>
        mode === "CASH" ? data.openingBalanceCash : data.openingBalanceOnline,
      colorPositive: "success.main",
      colorNegative: "error.main",
    },
    {
      key: "closingBalance",
      label: "CLOSING BALANCE",
      getValue: (data: Summary, mode: string) =>
        mode === "CASH" ? data.closingBalanceCash : data.closingBalanceOnline,
      colorPositive: "success.main",
      colorNegative: "error.main",
    },
  ];

  const bank = selectedType?.bankDetails;

  if (typesLoading || loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Title title="Yearly Ledger Dashboard" />
        <CircularProgress />
      </Box>
    );
  }

  if (ledgerTypes.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Title title="Yearly Ledger Dashboard" />
        <Typography color="text.secondary">
          No ledger types configured. Add ledger types under Yearly Ledger Settings.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Yearly Ledger Dashboard" />
      <Typography variant="h5" gutterBottom>
        Yearly Ledger
      </Typography>
      <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Tabs
          value={yearlyLedgerTypeId}
          onChange={(_e, v) => setYearlyLedgerTypeId(v as string)}
          sx={{ minHeight: 40 }}
        >
          {ledgerTypes.map((ledgerType) => (
            <Tab key={ledgerType.id} label={ledgerType.name} value={ledgerType.id} />
          ))}
        </Tabs>
        <TextField
          select
          label="Financial Year"
          value={financialYear}
          onChange={(e) => setFinancialYear(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
        >
          {generateFinancialYearOptions().map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={() => void fetchLedgerReport()}>
          Refresh
        </Button>
      </Box>

      {bank &&
        (bank.bankName ||
          bank.accountNumber ||
          bank.ifscCode ||
          bank.upiId ||
          bank.accountName) && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Bank details ({selectedType?.name})
              </Typography>
              <Typography variant="body2">
                {[bank.bankName, bank.branchName].filter(Boolean).join(" · ") || "—"}
              </Typography>
              {bank.accountName && (
                <Typography variant="body2">Account name: {bank.accountName}</Typography>
              )}
              {bank.accountNumber && (
                <Typography variant="body2">Account no: {bank.accountNumber}</Typography>
              )}
              {bank.ifscCode && <Typography variant="body2">IFSC: {bank.ifscCode}</Typography>}
              {bank.upiId && <Typography variant="body2">UPI: {bank.upiId}</Typography>}
            </CardContent>
          </Card>
        )}

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={paymentModeTab}
          onChange={(_e, v) => setPaymentModeTab(v as "CASH" | "ONLINE")}
        >
          <Tab label="CASH" value="CASH" />
          <Tab label="ONLINE" value="ONLINE" />
        </Tabs>
      </Box>

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summaryCardsConfig.map((card) => {
            const value = card.getValue(summary, paymentModeTab) ?? 0;
            const color = value >= 0 ? card.colorPositive : card.colorNegative;
            return (
              <Grid key={card.key} size={{ xs: 6, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      {card.label}
                    </Typography>
                    <Typography variant="h5" color={color}>
                      {formatCurrency(value)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Ledger Entries - {summary?.entryCount ?? 0}
          </Typography>
          <List
            resource="yearly-ledger"
            disableSyncWithLocation
            perPage={50}
            filter={{
              financialYear,
              yearlyLedgerTypeId,
              paymentMode: paymentModeTab,
              receiptDate_gte: fyDateRange.startDate,
              receiptDate_lte: fyDateRange.endDate,
            }}
            sort={{ field: "receiptDate", order: "DESC" }}
            exporter={canView ? (exporter as unknown as Exporter) : false}
            actions={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  width: "100%",
                  p: 1,
                  gap: 1,
                }}
              >
                {canView ? <ExportButton size="small" /> : null}
                {canCreate ? (
                  <Button variant="contained" color="primary" onClick={() => redirect(createUrl)}>
                    Create
                  </Button>
                ) : null}
              </Box>
            }
            empty={
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  No entries found for the selected filters.
                </Typography>
                {canCreate ? (
                  <Button variant="contained" onClick={() => redirect(createUrl)}>
                    Create
                  </Button>
                ) : null}
              </Box>
            }
          >
            {isMobile ? (
              <Datagrid bulkActionButtons={false} rowClick={false}>
                <RATextField source="receiptDate" label="Date" />
                <RATextField source="receiptType" label="Type" />
                <RATextField source="paymentMode" label="Mode" />
                <RATextField source="amount" label="Amount" />
                <RATextField source="purpose" label="Purpose" />
                {printEntryField}
              </Datagrid>
            ) : (
              <Datagrid bulkActionButtons={false}>
                <FunctionField
                  label="Date"
                  source="receiptDate"
                  render={(record) =>
                    record.receiptDate ? (
                      <span>{formatListDate(String(record.receiptDate))}</span>
                    ) : (
                      <span>—</span>
                    )
                  }
                />
                <RATextField source="receiptType" label="Type" />
                <RATextField source="paymentMode" label="Payment Mode" />
                <FunctionField
                  label="Amount"
                  render={(record) => (
                    <Typography
                      variant="body2"
                      color={record.receiptType === "CREDIT" ? "success.main" : "error.main"}
                      fontWeight="bold"
                    >
                      {record.receiptType === "CREDIT" ? "+" : "-"}
                      {formatCurrency(Number(record.amount ?? 0))}
                    </Typography>
                  )}
                />
                <RATextField source="purpose" label="Purpose" />
                <RATextField source="remarks" label="Remarks" />
                {printEntryField}
              </Datagrid>
            )}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
}
